import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { ConfigProvider, DatePicker, theme as antTheme } from "antd";
import { SwapOutlined } from "@ant-design/icons";
import { useTheme } from "../context/ThemeContext";

const { RangePicker } = DatePicker;

const CHIPS = ["D", "W", "1M", "3M", "6M", "YTD", "1Y"];

function chipToRange(chip, today = dayjs()) {
  const end = dayjs(today);
  let start = dayjs(today);

  switch (chip) {
    case "D":
      start = end.subtract(1, "day");
      break;

    case "W":
      start = end.subtract(7, "day");
      break;

    case "1M":
      start = end.subtract(1, "month");
      break;

    case "3M":
      start = end.subtract(3, "month");
      break;

    case "6M":
      start = end.subtract(6, "month");
      break;

    case "YTD":
      start = end.startOf("year"); // January 1st
      break;

    case "1Y":
      start = end.subtract(1, "year");
      break;

    default:
      break;
  }

  return {
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
  };
}

const defaultRange = chipToRange("1M");

export const DEFAULT_FILTERS = {
  account: [],
  meter: [],
  category: [],
  mode: "Interval",
  chip: "1M",
  start: defaultRange.start,
  end: defaultRange.end,
  interval: "Day",
};

function MultiSelect({
  label,
  placeholder,
  options = [],
  valueKey,
  labelKey,
  selected = [],
  onChange,
  onCloseCommit = () => {},
  colors,
}) {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [allOptions, setAllOptions] = useState(options || []);
  const [openSelectedSnapshot, setOpenSelectedSnapshot] = useState(
    selected || [],
  );
  const ref = useRef(null);

  const hasChangedRef = useRef(false);
  const latestSelectedRef = useRef(selected);
  const mergeUniqueOptions = (oldOptions = [], newOptions = []) => {
    const map = new Map();

    [...oldOptions, ...newOptions].forEach((item) => {
      const key = item?.[valueKey];

      if (key !== undefined && key !== null && key !== "") {
        map.set(String(key), item);
      }
    });

    return Array.from(map.values());
  };

  useEffect(() => {
    setAllOptions((prev) => {
      // When parent clears dropdown fully, reset cached options also
      if ((options || []).length === 0 && (selected || []).length === 0) {
        return [];
      }

      // Keep old full list + add latest API options
      return mergeUniqueOptions(prev, options || []);
    });
  }, [options, selected?.length, valueKey]);

  const isSelectedValue = (id) =>
    (selected || []).some((item) => String(item) === String(id));

  const isSnapshotSelectedValue = (id) =>
    (openSelectedSnapshot || []).some((item) => String(item) === String(id));

  const getSnapshotSelectedIndex = (id) =>
    (openSelectedSnapshot || []).findIndex(
      (item) => String(item) === String(id),
    );

  useEffect(() => {
    latestSelectedRef.current = selected;
  }, [selected]);

  // Keep original full dropdown list
  // const [allOptions, setAllOptions] = useState(options);

  // useEffect(() => {
  //   if (options.length > allOptions.length) {
  //     setAllOptions(options);
  //   }

  //   if (allOptions.length === 0 && options.length > 0) {
  //     setAllOptions(options);
  //   }
  // }, [options, allOptions.length]);
  const closeDropdown = () => {
    if (hasChangedRef.current) {
      onCloseCommit(latestSelectedRef.current);
      hasChangedRef.current = false;
    }
    setSearchText("");
    setOpen(false);
  };

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        if (open) {
          closeDropdown();
        }
      }
    };

    document.addEventListener("mousedown", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [open]);

  const toggle = (id) => {
    hasChangedRef.current = true;

    if (isSelectedValue(id)) {
      onChange((selected || []).filter((x) => String(x) !== String(id)));
    } else {
      onChange([...(selected || []), id]);
    }

    setOpen(true);
  };

  const clearAll = (e) => {
    e.stopPropagation();

    hasChangedRef.current = true;
    onChange([]);

    setTimeout(() => {
      onCloseCommit([]);
      hasChangedRef.current = false;
      setOpen(false);
    }, 0);
  };

  const dropdownOptions = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();

    const sourceOptions = allOptions.length > 0 ? allOptions : options;

    const filteredOptions = sourceOptions.filter((opt) => {
      const labelValue = String(opt?.[labelKey] ?? "").toLowerCase();
      const idValue = String(opt?.[valueKey] ?? "").toLowerCase();

      if (!keyword) return true;

      return labelValue.includes(keyword) || idValue.includes(keyword);
    });

    return [...filteredOptions].sort((a, b) => {
      const aId = a?.[valueKey];
      const bId = b?.[valueKey];

      const aSelected = isSnapshotSelectedValue(aId);
      const bSelected = isSnapshotSelectedValue(bId);

      // Selected values from opening-time snapshot first
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // Keep selected values in same order as snapshot
      if (aSelected && bSelected) {
        return getSnapshotSelectedIndex(aId) - getSnapshotSelectedIndex(bId);
      }

      // Non-selected values keep original API/cache order
      return 0;
    });
  }, [
    allOptions,
    options,
    searchText,
    labelKey,
    valueKey,
    openSelectedSnapshot,
  ]);

  const displayText =
    selected.length === 0
      ? placeholder
      : selected.length > 1
        ? `${selected.length} selected`
        : (() => {
            const sourceOptions = allOptions.length > 0 ? allOptions : options;

            const opt = sourceOptions.find(
              (o) => String(o[valueKey]) === String(selected[0]),
            );

            return opt ? opt[labelKey] : selected[0];
          })();

  return (
    <div className="relative" ref={ref}>
      <div
        className="mb-2 text-xs uppercase tracking-wide"
        style={{ color: colors.textSecondary }}
      >
        {label}
      </div>

      <button
        type="button"
        onClick={() => {
          if (open) {
            closeDropdown();
          } else {
            setOpenSelectedSnapshot(selected || []);
            setOpen(true);
          }
        }}
        className="flex min-h-[44px] w-full items-center justify-between rounded-md border px-3 py-2 text-left"
        style={{
          backgroundColor: colors.background,
          borderColor: colors.border,
        }}
      >
        <div className="min-w-0 flex-1 overflow-hidden">
          {selected.length === 0 ? (
            <span
              className="block truncate text-sm"
              style={{ color: colors.text }}
            >
              {displayText}
            </span>
          ) : (
            <span
              className="inline-block max-w-full truncate rounded-full px-3 py-1 text-xs"
              style={{
                backgroundColor: `${colors.primary}20`,
                color: colors.primary,
              }}
            >
              {displayText}
            </span>
          )}
        </div>

        <span className="ml-2 text-xs" style={{ color: colors.textSecondary }}>
          ▼
        </span>
      </button>

      {open && (
        <div
          className="absolute left-0 top-full z-[9999] mt-1 w-full overflow-hidden rounded-md border shadow-xl"
          style={{
            backgroundColor: colors.background,
            borderColor: colors.border,
          }}
        >
          <div
            className="border-b p-2"
            style={{ borderColor: colors.border }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}...`}
                className="h-[36px] w-full rounded-md border px-3 pr-8 text-sm outline-none"
                style={{
                  backgroundColor: colors.cardBg,
                  borderColor: colors.border,
                  color: colors.text,
                }}
                autoFocus
              />

              {searchText && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchText("");
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-sm font-bold cursor-pointer"
                  style={{ color: colors.textSecondary }}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={clearAll}
            className="block w-full border-b px-4 py-2 text-left text-sm transition-all"
            style={{
              borderColor: colors.border,
              color: colors.primary,
            }}
          >
            × Clear all
          </button>

          <div className="max-h-[220px] overflow-y-auto">
            {dropdownOptions.length === 0 ? (
              <div
                className="px-4 py-3 text-sm"
                style={{ color: colors.textSecondary }}
              >
                No matching {label.toLowerCase()} found.
              </div>
            ) : (
              dropdownOptions.map((opt) => {
                const id = opt[valueKey];
                const checked = isSelectedValue(id);

                return (
                  <button
                    type="button"
                    key={id}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(id);
                    }}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left transition-all"
                    style={{
                      color: colors.text,
                    }}
                  >
                    <span
                      className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border"
                      style={{
                        borderColor: checked
                          ? colors.primary
                          : colors.textSecondary,
                        backgroundColor: checked
                          ? colors.primary
                          : colors.background,
                      }}
                    >
                      {checked && (
                        <span className="text-[11px] leading-none text-white">
                          ✓
                        </span>
                      )}
                    </span>

                    <span className="truncate text-sm font-medium">
                      {opt[labelKey]}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
export default function WaterUsageFilters({
  accounts = [],
  meters = [],
  categories = [],
  value = DEFAULT_FILTERS,
  onChange = () => {},
  onOptionFilterChange = async () => null,
  onIntervalChange = () => {},
  onDownload = () => {},
}) {
  const { colors, theme } = useTheme();

  const appliedFilters = useMemo(
    () => ({
      ...DEFAULT_FILTERS,
      ...value,
    }),
    [value],
  );

  const [draftFilters, setDraftFilters] = useState(appliedFilters);
  const latestCommitRef = useRef(0);
  const [frontendOnlySource, setFrontendOnlySource] = useState(null);
  useEffect(() => {
    setDraftFilters(appliedFilters);
  }, [appliedFilters]);

  const f = draftFilters;

  const getApplyCompareKey = (filters = {}) =>
    JSON.stringify({
      account: filters.account || [],
      meter: filters.meter || [],
      category: filters.category || [],
      mode: filters.mode,
      chip: filters.chip,
      start: filters.start,
      end: filters.end,
    });

  const hasPendingApplyChanges =
    getApplyCompareKey(draftFilters) !== getApplyCompareKey(appliedFilters);

  // useEffect(() => {
  //   if (f.chip && (!f.start || !f.end)) {
  //     const { start, end } = chipToRange(f.chip);

  //     onChange({
  //       ...f,
  //       start,
  //       end,
  //     });
  //   }

  // }, []);

  // const set = (patch) => {
  //   onChange({
  //     ...f,
  //     ...patch,
  //   });
  // };
  // Inside WaterUsageFilters.jsx

  const set = (patch) => {
    setDraftFilters((prev) => ({
      ...prev,
      ...patch,
    }));
  };

  const onDateInput = (key, val) => {
    if (!val) return;

    set({
      [key]: val,
      mode: "Custom",
      chip: null,
    });
  };

  const onChipClick = (chip) => {
    const { start, end } = chipToRange(chip);

    const nextInterval = getValidIntervalForChip(chip, f.interval);

    set({
      chip,
      mode: "Interval",
      start,
      end,
      interval: nextInterval,
    });
  };

  const onModeClick = (mode) => {
    if (mode === "Custom") {
      set({
        mode: "Custom",
        chip: null,
      });
      return;
    }

    set({
      mode,
      chip: f.chip || "1M",
    });
  };

  const setTopFilter = (patch) => {
    setDraftFilters((prev) => ({
      ...prev,
      ...patch,
    }));
  };

  const commitTopFilter = async (patch, changedBy) => {
    const commitId = Date.now();
    latestCommitRef.current = commitId;

    const nextFilters = {
      ...draftFilters,
      ...patch,
    };

    setDraftFilters(nextFilters);

    const finalFilters = await onOptionFilterChange(
      {
        account: nextFilters.account || [],
        meter: nextFilters.meter || [],
        category: nextFilters.category || [],
      },
      changedBy,
    );

    // Ignore old response
    if (latestCommitRef.current !== commitId) return;

    if (finalFilters) {
      setDraftFilters((prev) => ({
        ...prev,
        account: finalFilters.account || [],
        meter: finalFilters.meter || [],
        category: finalFilters.category || [],
      }));
    }
  };

  const handleApply = () => {
    let finalFilters = { ...draftFilters };

    if (frontendOnlySource === "category") {
      finalFilters = {
        ...draftFilters,
        account: [],
        meter: [],
        category: draftFilters.category || [],
      };
    }

    if (frontendOnlySource === "account") {
      finalFilters = {
        ...draftFilters,
        account: draftFilters.account || [],
        meter: [],
        category: [],
      };
    }

    if (frontendOnlySource === "account_category") {
      finalFilters = {
        ...draftFilters,
        account: draftFilters.account || [],
        category: draftFilters.category || [],
        meter: [],
      };
    }

    onChange(finalFilters);
  };

  const handleCancel = () => {
    const resetFilters = {
      ...DEFAULT_FILTERS,
    };

    setFrontendOnlySource(null);
    setDraftFilters(resetFilters);
    onChange(resetFilters);

    onOptionFilterChange({
      account: [],
      meter: [],
      category: [],
    });
  };

  // const onDateInput = (key, val) => {
  //   set({
  //     [key]: val,
  //     mode: "Custom",
  //     chip: null,
  //   });
  // };
  // const onDateInput = (key, val) => {
  //   // We update the local object and immediately push the whole state up
  //   const newFilters = {
  //     ...f,
  //     [key]: val,
  //     mode: "Custom",
  //     chip: null,
  //   };
  //   onChange(newFilters);
  // };

  const getValidIntervalForChip = (chip, currentInterval) => {
    // Past Day: hide Week, so if current is Week, change to Day
    if (chip === "D" && currentInterval === "Week") {
      return "Day";
    }

    // 1Y: hide Hour, so if current is Hour, change to Week
    if (chip === "1Y" && currentInterval === "Hour") {
      return "Week";
    }

    return currentInterval || "Day";
  };

  const shouldShowInterval = (interval) => {
    // Past Day selected => hide Week
    if (f.chip === "D" && interval === "Week") {
      return false;
    }

    // 1Y selected => hide Hour
    if (f.chip === "1Y" && interval === "Hour") {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (f.chip === "D" && f.interval === "Week") {
      setDraftFilters((prev) => ({
        ...prev,
        interval: "Day",
      }));
    }

    if (f.chip === "1Y" && f.interval === "Hour") {
      setDraftFilters((prev) => ({
        ...prev,
        interval: "Week",
      }));
    }
  }, [f.chip, f.interval]);

  const selectedCategoryCodes = f.category || [];

  const categoryMatchedAccounts = useMemo(() => {
    if (!selectedCategoryCodes.length) return [];
    return (accounts || []).map((acc) => acc.id);
  }, [accounts, selectedCategoryCodes.length]);

  const categoryMatchedMeters = useMemo(() => {
    if (!selectedCategoryCodes.length) return [];
    return (meters || []).map((meter) => meter.id);
  }, [meters, selectedCategoryCodes.length]);

  const selectedAccountIds = f.account || [];

  const accountMatchedMeters = useMemo(() => {
    if (!selectedAccountIds.length) return [];
    return (meters || []).map((meter) => meter.id);
  }, [meters, selectedAccountIds.length]);

  const accountMatchedCategories = useMemo(() => {
  if (!selectedAccountIds.length) return [];
  return (categories || []).map((cat) => cat.code);
}, [categories, selectedAccountIds.length]);

  const accountCategoryMatchedMeters = useMemo(() => {
    if (!selectedAccountIds.length || !selectedCategoryCodes.length) return [];
    return (meters || []).map((meter) => meter.id);
  }, [meters, selectedAccountIds.length, selectedCategoryCodes.length]);

  return (
    <div
      className="mt-5 overflow-visible rounded-xl border"
      style={{
        backgroundColor: colors.cardBg,
        borderColor: colors.border,
      }}
    >
      {/* TOP FILTERS */}
      <div
        className="grid grid-cols-1 gap-4 border-b p-4 md:grid-cols-3"
        style={{ borderColor: colors.border }}
      >
        <MultiSelect
          label="Accounts"
          placeholder={`All accounts${accounts.length ? ` (${accounts.length})` : ""}`}
          options={accounts}
          valueKey="id"
          labelKey="name"
          selected={
            frontendOnlySource === "category" && selectedCategoryCodes.length
              ? categoryMatchedAccounts
              : f.account || []
          }
          onChange={(account) => {
            setFrontendOnlySource("account");
            setTopFilter({
              account,
              meter: [],
              category: [],
            });
          }}
          onCloseCommit={(account) => {
            setFrontendOnlySource("account");
            commitTopFilter(
              {
                account,
                meter: [],
                category: [],
              },
              "account",
            );
          }}
          colors={colors}
        />

        <MultiSelect
          label="Meters"
          placeholder={`All meters${meters.length ? ` (${meters.length})` : ""}`}
          options={meters}
          valueKey="id"
          labelKey="name"
          selected={
            frontendOnlySource === "category" && selectedCategoryCodes.length
              ? categoryMatchedMeters
              : frontendOnlySource === "account" && selectedAccountIds.length
                ? accountMatchedMeters
                : frontendOnlySource === "account_category" &&
                    selectedAccountIds.length &&
                    selectedCategoryCodes.length
                  ? accountCategoryMatchedMeters
                  : f.meter || []
          }
          onChange={(meter) => {
            setFrontendOnlySource(null);
            setTopFilter({
              meter,
              account: [],
              category: [],
            });
          }}
          onCloseCommit={(meter) => {
            setFrontendOnlySource(null);
            commitTopFilter(
              {
                meter,
                account: [],
                category: [],
              },
              "meter",
            );
          }}
          colors={colors}
        />

        <MultiSelect
          label="Category"
          placeholder={`All Category ${categories.length ? ` (${categories.length})` : ""}`}
          options={categories}
          valueKey="code"
          labelKey="name"
          selected={
            frontendOnlySource === "account" && selectedAccountIds.length
              ? accountMatchedCategories
              : f.category || []
          }
          onChange={(category) => {
            if (
              (frontendOnlySource === "account" ||
                frontendOnlySource === "account_category") &&
              selectedAccountIds.length
            ) {
              setFrontendOnlySource("account_category");
              setTopFilter({
                account: selectedAccountIds,
                category,
                meter: [],
              });
              return;
            }

            setFrontendOnlySource("category");
            setTopFilter({
              category,
              account: [],
              meter: [],
            });
          }}
          onCloseCommit={(category) => {
            if (
              (frontendOnlySource === "account" ||
                frontendOnlySource === "account_category") &&
              selectedAccountIds.length
            ) {
              setFrontendOnlySource("account_category");
              commitTopFilter(
                {
                  account: selectedAccountIds,
                  category,
                  meter: [],
                },
                "category",
              );
              return;
            }

            setFrontendOnlySource("category");
            commitTopFilter(
              {
                category,
                account: [],
                meter: [],
              },
              "category",
            );
          }}
          colors={colors}
        />
      </div>

      {/* BOTTOM FILTERS */}
      <div className="flex flex-wrap items-end justify-between gap-5 p-4">
        <div>
          <div className="flex flex-wrap gap-2">
            {CHIPS.map((chip) => (
              <button
                type="button"
                key={chip}
                onClick={() => onChipClick(chip)}
                className="rounded-md px-4 py-2 text-sm transition-all text-[12px]"
                style={{
                  backgroundColor:
                    f.chip === chip ? colors.primary : colors.background,
                  color:
                    f.chip === chip
                      ? colors.buttonText || "#ffffff"
                      : colors.textSecondary,
                  border:
                    f.chip === chip
                      ? `1px solid ${colors.primary}`
                      : `1px solid ${colors.border}`,
                }}
              >
                {chip}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div
              className="h-[40px] flex items-center rounded-md border px-2"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border,
              }}
            >
              <ConfigProvider
                theme={{
                  algorithm:
                    theme === "white"
                      ? antTheme.defaultAlgorithm
                      : antTheme.darkAlgorithm,
                  token: {
                    colorPrimary: colors.primary,
                    colorBgContainer: colors.background,
                    colorText: colors.text,
                    colorTextPlaceholder: colors.textSecondary,
                    colorBorder: "transparent",
                    controlHeight: 36,
                    borderRadius: 8,
                  },
                }}
              >
                <RangePicker
                  value={[
                    f.start ? dayjs(f.start, "YYYY-MM-DD") : null,
                    f.end ? dayjs(f.end, "YYYY-MM-DD") : null,
                  ]}
                  onChange={(dates, dateStrings) => {
                    const [start, end] = dateStrings;

                    if (start && end) {
                      set({
                        start,
                        end,
                        mode: "Custom",
                        chip: null,
                      });
                    }
                  }}
                  disabledDate={(current) =>
                    current && current > dayjs().endOf("day")
                  }
                  placeholder={["Start", "End"]}
                  variant="borderless"
                  separator={
                    <SwapOutlined style={{ color: colors.textSecondary }} />
                  }
                  className="custom-range-picker cursor-pointer"
                  style={{
                    width: "240px",
                    background: "transparent",
                    color: colors.text,
                  }}
                  classNames={{
                    popup: {
                      root: "mizu-calendar-popup",
                    },
                  }}
                />
              </ConfigProvider>
            </div>
          </div>

          <div>
            {/* <div
              className="mb-2 text-xs font-semibold text-[12px] tracking-wide"
              style={{ color: colors.textSecondary }}
            >
              Interval
            </div> */}

            <div
              className="flex overflow-hidden rounded-md border text-[12px]"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border,
              }}
            >
              {["Hour", "Day", "Week"]
                .filter(shouldShowInterval)
                .map((interval) => (
                  <button
                    type="button"
                    key={interval}
                    onClick={() => {
                      set({ interval });
                      if (!hasPendingApplyChanges) {
                        onIntervalChange(interval);
                      }
                    }}
                    className="px-4 py-2 text-sm transition-all"
                    style={{
                      backgroundColor:
                        f.interval === interval
                          ? colors.primary
                          : "transparent",
                      color:
                        f.interval === interval
                          ? colors.buttonText || "#ffffff"
                          : colors.textSecondary,
                    }}
                  >
                    {interval}
                  </button>
                ))}
            </div>
          </div>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="h-[40px] rounded-md border px-5 text-sm font-semibold transition-all cursor-pointer"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border,
                color: colors.textSecondary,
              }}
            >
              Clear
            </button>

            <button
              type="button"
              onClick={handleApply}
              className="h-[40px] rounded-md border px-5 text-sm font-semibold transition-all cursor-pointer"
              style={{
                backgroundColor: colors.primary,
                borderColor: colors.primary,
                color: colors.buttonText || "#ffffff",
              }}
            >
              Apply
            </button>
          </div>
          <button
            type="button"
            onClick={onDownload}
            className="h-[40px] w-[40px] rounded-md border transition-all cursor-pointer"
            style={{
              backgroundColor: colors.background,
              borderColor: colors.border,
              color: colors.textSecondary,
            }}
          >
            ↓
          </button>
        </div>
      </div>
    </div>
  );
}
